import React from 'react';

/**
 * The component gallery.
 *
 * Every sample is built from the SEMANTIC tokens rather than raw palette
 * classes, so it shows what the theme actually does. Anything drawn here with
 * bg-white or text-gray-700 would sit there looking unaffected while the rest of
 * the app changed - which is exactly the kind of preview that lies.
 *
 * Only the mode being edited renders. The page switches the whole document to
 * that mode, so the gallery, the page samples beside it and the app around them
 * all agree - and the controls next to it move exactly what is on screen.
 */

export interface GallerySection {
  id: string;
  label: string;
  /** Component tokens this section is wired to, for the editor to scope to. */
  tokens: string[];
  /**
   * False while the real component has not been migrated to these tokens yet.
   * The section still previews, but the editor says so - a control that edits
   * only the sample is a promise the page cannot keep, and the honesty test in
   * galleryWiring.test.ts fails if a wired section lists an unconsumed token OR
   * an unwired section quietly gains real wiring without flipping this flag.
   */
  wired: boolean;
}

export const GALLERY_SECTIONS: GallerySection[] = [
  { id: 'page-header', label: 'Page header', wired: true, tokens: ['cmp-page-header-bg', 'cmp-page-header-fg', 'cmp-page-header-border'] },
  { id: 'table', label: 'Data grid', wired: true, tokens: ['cmp-table-titlebar-bg', 'cmp-table-titlebar-fg', 'cmp-table-header-bg', 'cmp-table-header-fg', 'cmp-table-row-hover'] },
  { id: 'modal', label: 'Modal', wired: true, tokens: ['cmp-modal-header-bg', 'cmp-modal-header-fg', 'cmp-modal-header-border', 'cmp-modal-header-muted', 'cmp-modal-bg'] },
  { id: 'shell', label: 'Nav + top bar', wired: true, tokens: ['cmp-topbar-bg', 'cmp-topbar-fg', 'cmp-topbar-border', 'cmp-sidebar-bg', 'cmp-sidebar-fg', 'cmp-sidebar-muted', 'cmp-sidebar-active-fg', 'cmp-sidebar-border'] },
  { id: 'drawer', label: 'Drawer', wired: true, tokens: ['cmp-drawer-header-bg', 'cmp-drawer-header-fg', 'cmp-drawer-bg'] },
  { id: 'tabs', label: 'Tabs', wired: true, tokens: ['cmp-tab-fg', 'cmp-tab-active-fg', 'cmp-tab-active-border'] },
  { id: 'feedback', label: 'Toast + tooltip', wired: true, tokens: ['cmp-tooltip-bg', 'cmp-tooltip-fg', 'cmp-tooltip-border'] },
  { id: 'surfaces', label: 'Surface scale', wired: true, tokens: ['sem-surface-canvas', 'sem-surface-sunken', 'sem-surface-panel', 'sem-surface-raised', 'sem-surface-hover'] },
  { id: 'text', label: 'Type scale', wired: true, tokens: ['sem-text-heading', 'sem-text-body', 'sem-text-muted', 'sem-text-label', 'sem-text-disabled'] },
  // accent-active and surface-selected stay OFF this list until something real
  // consumes them - the samples still render, but a token named here is a claim
  // that editing it changes the app.
  { id: 'states', label: 'Interaction states', wired: true, tokens: ['sem-accent', 'sem-accent-hover', 'sem-focus-ring'] },
  // Not yet: these components have no chokepoint to migrate (no shared Button
  // or Card exists) or their primitives are still Phase 3 backlog.
  { id: 'form', label: 'Form fields', wired: true, tokens: ['cmp-field-bg', 'cmp-field-fg', 'cmp-field-border', 'cmp-field-label-fg'] },
  // These three edit the SEMANTIC tokens their real components read, not the
  // per-component geometry slots - those exist in CSS but nothing consumes them,
  // so offering them would be a control that moves only the sample. Buttons is
  // named for what it actually governs: the primary-button recipe, which is the
  // accent.
  { id: 'buttons', label: 'Primary buttons', wired: true, tokens: ['sem-accent', 'sem-accent-hover', 'sem-accent-fg'] },
  { id: 'badges', label: 'Status badges', wired: true, tokens: ['sem-ok-bg', 'sem-ok-fg', 'sem-warn-bg', 'sem-warn-fg', 'sem-danger-bg', 'sem-danger-fg', 'sem-info-bg', 'sem-info-fg', 'sem-flag-bg', 'sem-flag-fg'] },
  { id: 'card', label: 'Card', wired: true, tokens: ['sem-surface-panel', 'sem-border'] },
];

const btn = 'inline-flex items-center gap-2 font-medium';
const btnStyle: React.CSSProperties = {
  borderRadius: 'var(--cmp-button-radius)',
  paddingLeft: 'var(--cmp-button-pad-x)',
  paddingRight: 'var(--cmp-button-pad-x)',
  paddingTop: 'var(--cmp-button-pad-y)',
  paddingBottom: 'var(--cmp-button-pad-y)',
  fontSize: 'var(--cmp-button-font-size)',
  fontWeight: 'var(--cmp-button-font-weight)' as unknown as number,
};

const badgeStyle: React.CSSProperties = {
  borderRadius: 'var(--cmp-badge-radius)',
  paddingLeft: 'var(--cmp-badge-pad-x)',
  paddingRight: 'var(--cmp-badge-pad-x)',
  paddingTop: 'var(--cmp-badge-pad-y)',
  paddingBottom: 'var(--cmp-badge-pad-y)',
  fontSize: 'var(--cmp-badge-font-size)',
};

const Samples: React.FC<{ selected: string | null }> = ({ selected }) => {
  const show = (id: string) => selected === null || selected === id;
  const ring = (id: string) => (selected === id ? 'ring-2 ring-accent rounded' : '');

  return (
    <div className="space-y-4 bg-app p-4 text-body">
      {show('page-header') && (
        <div className={ring('page-header')}>
          <div
            className="border-b px-4 py-3"
            style={{
              background: 'rgb(var(--cmp-page-header-bg))',
              color: 'rgb(var(--cmp-page-header-fg))',
              borderColor: 'rgb(var(--cmp-page-header-border))',
            }}
          >
            <div className="text-base font-semibold">PRO 1234567</div>
            <div className="text-xs text-muted">Import · Houston · Delivered</div>
          </div>
        </div>
      )}

      {show('table') && (
        <div className={`overflow-hidden rounded border border-rule ${ring('table')}`}>
          <div
            className="px-3 py-2 text-sm font-semibold"
            style={{
              background: 'rgb(var(--cmp-table-titlebar-bg))',
              color: 'rgb(var(--cmp-table-titlebar-fg))',
            }}
          >
            Active Orders
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr style={{
                background: 'rgb(var(--cmp-table-header-bg))',
                color: 'rgb(var(--cmp-table-header-fg))',
              }}
              >
                <th className="px-3 py-2 text-left font-semibold">PRO</th>
                <th className="px-3 py-2 text-left font-semibold">Customer</th>
                <th className="px-3 py-2 text-left font-semibold">Status</th>
              </tr>
            </thead>
            <tbody className="bg-panel">
              {[['1234567', 'Acme Freight', 'ok'], ['1234568', 'Gulf Imports', 'warn']].map(
                ([pro, cust, tone]) => (
                  <tr key={pro} className="border-t border-hairline">
                    <td className="px-3 py-2 tabular-nums">{pro}</td>
                    <td className="px-3 py-2">{cust}</td>
                    <td className="px-3 py-2">
                      <span
                        style={badgeStyle}
                        className={tone === 'ok' ? 'bg-ok text-ok-fg' : 'bg-warn text-warn-fg'}
                      >
                        {tone === 'ok' ? 'Delivered' : 'At risk'}
                      </span>
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>
      )}

      {show('modal') && (
        <div
          className={`overflow-hidden border border-rule ${ring('modal')}`}
          style={{ borderRadius: 'var(--cmp-modal-radius)', background: 'rgb(var(--cmp-modal-bg))' }}
        >
          <div
            className="flex items-center justify-between px-4 py-2 text-sm font-semibold"
            style={{
              background: 'rgb(var(--cmp-modal-header-bg))',
              color: 'rgb(var(--cmp-modal-header-fg))',
            }}
          >
            <span>Confirm changes</span>
            <span aria-hidden>×</span>
          </div>
          <div className="p-4 text-sm">
            <p className="text-body">Save these changes to the order?</p>
            <div className="mt-3 flex justify-end gap-2">
              <button type="button" style={btnStyle} className={`${btn} border border-rule text-body`}>
                Cancel
              </button>
              <button type="button" style={btnStyle} className={`${btn} bg-accent text-accent-fg`}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {show('form') && (
        <div className={`space-y-2 rounded border border-rule bg-panel p-4 ${ring('form')}`}>
          <label className="block text-xs font-bold" style={{ color: 'rgb(var(--cmp-field-label-fg))' }}>
            Customer
          </label>
          <input
            readOnly
            value="Acme Freight"
            className="w-full px-2 py-1 text-sm"
            style={{
              background: 'rgb(var(--cmp-field-bg))',
              color: 'rgb(var(--cmp-field-fg))',
              border: '1px solid rgb(var(--cmp-field-border))',
              borderRadius: 'var(--cmp-input-radius)',
            }}
          />
          <input
            readOnly
            value="Focused field"
            className="w-full px-2 py-1 text-sm ring-2"
            style={{
              background: 'rgb(var(--cmp-field-bg))',
              color: 'rgb(var(--cmp-field-fg))',
              border: '1px solid rgb(var(--sem-focus-ring))',
              borderRadius: 'var(--cmp-input-radius)',
            }}
          />
        </div>
      )}

      {show('buttons') && (
        <div className={`flex flex-wrap items-center gap-2 ${ring('buttons')}`}>
          <button type="button" style={btnStyle} className={`${btn} bg-accent text-accent-fg`}>
            Primary
          </button>
          <button type="button" style={btnStyle} className={`${btn} border border-rule text-body`}>
            Secondary
          </button>
          <button type="button" style={btnStyle} className={`${btn} bg-danger text-danger-fg`}>
            Delete
          </button>
          <button type="button" disabled style={btnStyle} className={`${btn} border border-rule text-disabled`}>
            Disabled
          </button>
        </div>
      )}

      {show('badges') && (
        <div className={`flex flex-wrap items-center gap-2 ${ring('badges')}`}>
          <span style={badgeStyle} className="bg-ok text-ok-fg">Delivered</span>
          <span style={badgeStyle} className="bg-warn text-warn-fg">At risk</span>
          <span style={badgeStyle} className="bg-danger text-danger-fg">Failed</span>
          <span style={badgeStyle} className="bg-info text-info-fg">Booked</span>
          <span style={badgeStyle} className="border bg-flag text-flag-fg" >Changed</span>
        </div>
      )}

      {show('card') && (
        <div
          className={ring('card')}
          style={{
            background: 'rgb(var(--cmp-card-bg))',
            border: '1px solid rgb(var(--cmp-card-border))',
            borderRadius: 'var(--cmp-card-radius)',
            padding: 'var(--cmp-card-pad)',
          }}
        >
          <div className="text-sm font-semibold text-heading">Loads today</div>
          <div className="mt-1 text-2xl font-semibold tabular-nums text-heading">128</div>
          <div className="text-xs text-muted">12 awaiting dispatch</div>
        </div>
      )}

      {show('shell') && (
        <div className={`overflow-hidden rounded border border-rule ${ring('shell')}`}>
          <div
            className="flex items-center justify-between px-3 py-2 text-sm"
            style={{
              background: 'rgb(var(--cmp-topbar-bg))',
              color: 'rgb(var(--cmp-topbar-fg))',
              borderBottom: '1px solid rgb(var(--cmp-topbar-border))',
            }}
          >
            <span>Dispatching / At Risk Monitor</span>
            <span className="text-muted">JD</span>
          </div>
          <div className="flex">
            <div
              className="w-40 shrink-0 p-2 text-sm"
              style={{
                background: 'rgb(var(--cmp-sidebar-bg))',
                color: 'rgb(var(--cmp-sidebar-fg))',
                borderRight: '1px solid rgb(var(--cmp-sidebar-border))',
              }}
            >
              <div
                className="rounded px-2 py-1"
                style={{
                  background: 'rgb(var(--c-blue-600) / 0.1)',
                  color: 'rgb(var(--cmp-sidebar-active-fg))',
                }}
              >
                Dispatch Board
              </div>
              <div className="px-2 py-1" style={{ color: 'rgb(var(--cmp-sidebar-muted))' }}>
                Live Map
              </div>
              <div className="px-2 py-1" style={{ color: 'rgb(var(--cmp-sidebar-muted))' }}>
                Drivers
              </div>
            </div>
            <div className="flex-1 bg-app p-3 text-sm text-body">Page content</div>
          </div>
        </div>
      )}

      {show('drawer') && (
        <div className={`overflow-hidden rounded border border-rule ${ring('drawer')}`}>
          <div
            className="px-4 py-3 text-base font-semibold"
            style={{
              background: 'rgb(var(--cmp-drawer-header-bg))',
              color: 'rgb(var(--cmp-drawer-header-fg))',
              borderBottom: '1px solid rgb(var(--sem-border))',
            }}
          >
            Driver 4471
          </div>
          <div className="p-4 text-sm text-body" style={{ background: 'rgb(var(--cmp-drawer-bg))' }}>
            Hours remaining, current move, next appointment.
          </div>
        </div>
      )}

      {show('tabs') && (
        <div className={`border-b border-rule ${ring('tabs')}`}>
          <div className="flex gap-4 text-sm">
            <span
              className="border-b-2 pb-1 font-medium"
              style={{
                color: 'rgb(var(--cmp-tab-active-fg))',
                borderColor: 'rgb(var(--cmp-tab-active-border))',
              }}
            >
              Order Form
            </span>
            <span className="pb-1" style={{ color: 'rgb(var(--cmp-tab-fg))' }}>Invoicing</span>
            <span className="pb-1" style={{ color: 'rgb(var(--cmp-tab-fg))' }}>Documents</span>
            <span className="pb-1" style={{ color: 'rgb(var(--cmp-tab-fg))' }}>History</span>
          </div>
        </div>
      )}

      {show('surfaces') && (
        <div className={`grid grid-cols-5 gap-2 text-xs ${ring('surfaces')}`}>
          {([['Canvas', 'canvas'], ['Sunken', 'sunken'], ['Panel', 'panel'],
            ['Raised', 'raised'], ['Hover', 'hover']] as const).map(([label, key]) => (
              <div key={key} className="overflow-hidden rounded border border-rule">
                <div
                  className="h-10"
                  style={{ background: `rgb(var(--sem-surface-${key}))` }}
                />
                <div className="bg-panel px-1 py-0.5 text-center text-muted">{label}</div>
              </div>
          ))}
        </div>
      )}

      {show('text') && (
        <div className={`space-y-1 rounded border border-rule bg-panel p-4 ${ring('text')}`}>
          <div className="text-lg font-semibold text-heading">Heading text</div>
          <div className="text-sm text-body">Body text, the default for most content.</div>
          <div className="text-sm text-muted">Muted text for secondary detail.</div>
          <div className="text-xs font-medium text-label">FIELD LABEL</div>
          <div className="text-sm text-disabled">Disabled text</div>
        </div>
      )}

      {show('states') && (
        <div className={`flex flex-wrap items-center gap-2 ${ring('states')}`}>
          <button type="button" style={btnStyle} className={`${btn} bg-accent text-accent-fg`}>
            Default
          </button>
          <button type="button" style={btnStyle} className={`${btn} bg-accent-hover text-accent-fg`}>
            Hover
          </button>
          <button type="button" style={btnStyle} className={`${btn} bg-accent-active text-accent-fg`}>
            Active
          </button>
          <button type="button" style={btnStyle} className={`${btn} bg-accent text-accent-fg ring-2 ring-offset-2`}>
            Focused
          </button>
          <span
            className="rounded px-2 py-1 text-sm"
            style={{ background: 'rgb(var(--sem-surface-selected))', color: 'rgb(var(--sem-text-selected))' }}
          >
            Selected row
          </span>
        </div>
      )}

      {show('feedback') && (
        <div className={`flex flex-wrap items-center gap-3 ${ring('feedback')}`}>
          <div
            className="px-3 py-2 text-sm bg-ok text-ok-fg"
            style={{ borderRadius: 'var(--cmp-toast-radius)' }}
          >
            Order saved
          </div>
          <div
            className="px-2 py-1 text-xs"
            style={{
              background: 'rgb(var(--cmp-tooltip-bg))',
              color: 'rgb(var(--cmp-tooltip-fg))',
              borderRadius: 'var(--cmp-tooltip-radius)',
            }}
          >
            Tooltip text
          </div>
        </div>
      )}
    </div>
  );
};

interface GalleryProps {
  /** null shows everything; otherwise only that section, highlighted. */
  selected: string | null;
  /** The mode being edited. The gallery shows only that one. */
  mode: 'light' | 'dark';
}

/**
 * One mode, full width.
 *
 * This used to render light and dark side by side in a `.dark` wrapper. Two
 * columns halved the width of both, and the half that was not being edited was
 * the more distracting for it - the controls beside it move only one set of
 * values. The page now switches the document to the mode being edited, so what
 * the gallery shows and what the controls change are the same thing.
 */
export const Gallery: React.FC<GalleryProps> = ({ selected, mode }) => (
  <div className="overflow-hidden rounded border border-gray-200 dark:border-gray-700">
    <div className="border-b border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-semibold
                    uppercase tracking-wide text-gray-500 dark:border-gray-700
                    dark:bg-gray-800 dark:text-gray-400"
    >
      {mode} mode
    </div>
    <Samples selected={selected} />
  </div>
);

export default Gallery;
